/**
 * /welcome — the first-run wizard's door, and the ONLY guard this feature has.
 *
 * THE GUARD ONLY SENDS PEOPLE OUT (SPEC-09 §3.5.1):
 *
 *   - `VITE_ONBOARDING` off  → redirect to /boards. The flag is baked into the
 *     bundle at build time, so on an OFF build this route is a redirect and
 *     nothing else: no wizard chunk is ever loaded, no onboarding markup is
 *     ever rendered, and the rest of the app is byte-identical to the build
 *     before this file existed.
 *   - wizard already completed → redirect to /boards. Completion is recorded
 *     per user id in localStorage (SPEC-09 §3.5.5); the wizard is a first run,
 *     not a permanent tab.
 *
 * THERE IS NO GLOBAL REDIRECT-TRAP, AND THAT IS THE DESIGN. Nothing anywhere
 * pushes a signed-in person INTO /welcome. A returning login lands on /boards
 * exactly as it does today. The wizard is reached deliberately: signup sends
 * people here, invite-accept sends NEW people here with `?from=invite`, and the
 * "Setup guide" item in the sidebar account menu is the way back in while the
 * store still says incomplete. A first-run screen that catches everybody on
 * every login is the screen everybody learns to dismiss without reading.
 *
 * `validateSearch` lives in this (non-lazy) half because TanStack resolves
 * search params before the lazy component loads.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';

import { env } from '../../env';
import { isOnboardingComplete } from '../../components/onboarding/store';
import { useAuthStore } from '../../stores/auth-store';

export const Route = createFileRoute('/_authenticated/welcome')({
  validateSearch: (search: Record<string, unknown>): { from?: 'invite' } =>
    search.from === 'invite' ? { from: 'invite' } : {},
  beforeLoad: () => {
    if (!env.VITE_ONBOARDING) throw redirect({ to: '/boards' });
    const userId = useAuthStore.getState().user?.id ?? null;
    if (isOnboardingComplete(userId)) throw redirect({ to: '/boards' });
  },
}).lazy(() => import('./welcome.lazy').then((d) => d.Route));
